// Knowledge Base API用のReact Hook（非同期処理特化版）
import { useState, useCallback } from 'react';
import { 
  createChatSession, 
  addToSession,
  formatCitations,
  ChatSession 
} from '../services/knowledgeBaseService';
import { 
  useAsyncKnowledgeBase,
  UseAsyncKnowledgeBaseReturn 
} from './useAsyncKnowledgeBase';
import { KnowledgeBaseResponse, AsyncStatus } from '../types/ApiTypes';
import { appConfig } from '../config/env';

// Hook の戻り値の型定義（非同期処理特化）
export interface UseKnowledgeBaseReturn {
  // 状態
  loading: boolean;
  error: string | null;
  session: ChatSession;
  
  // 最新のレスポンス情報
  lastResponse: KnowledgeBaseResponse | null;
  citations: Array<{
    text: string;
    sources: Array<{
      content: string;
      metadata: Record<string, any>;
    }>;
  }>;
  
  // 非同期処理情報
  progress: string;
  currentStatus: AsyncStatus | null;
  requestId: string | null;
  
  // 関数（非同期処理のみ）
  sendMessageWithCitations: (message: string, idToken?: string, maxResults?: number) => Promise<KnowledgeBaseResponse>;
  startAsyncQuery: (message: string, idToken?: string, maxResults?: number) => Promise<string>;
  checkQueryStatus: (requestId: string, idToken?: string) => Promise<any>;
  clearSession: () => void;
  clearError: () => void;
  cancelQuery: () => void;
}

// Knowledge Base API用のカスタムフック（非同期処理特化）
export const useKnowledgeBase = (): UseKnowledgeBaseReturn => {
  const [session, setSession] = useState<ChatSession>(createChatSession());
  const [lastResponse, setLastResponse] = useState<KnowledgeBaseResponse | null>(null);
  const [citations, setCitations] = useState<Array<{
    text: string;
    sources: Array<{
      content: string;
      metadata: Record<string, any>;
    }>;
  }>>([]);

  // 非同期処理フックを統合
  const asyncKnowledgeBase = useAsyncKnowledgeBase();

  // 進捗情報を非同期処理フックから取得
  const progress = asyncKnowledgeBase.progress;
  const currentStatus = asyncKnowledgeBase.currentStatus;
  const requestId = asyncKnowledgeBase.requestId;
  const loading = asyncKnowledgeBase.loading;
  const error = asyncKnowledgeBase.error;

  // 完全な非同期処理（開始から完了まで）
  const sendMessageWithCitations = useCallback(async (
    message: string,
    idToken?: string,
    maxResults?: number
  ): Promise<KnowledgeBaseResponse> => {
    if (appConfig.debugMode) {
      console.log(`[DEBUG] useKnowledgeBase.sendMessageWithCitations: ${message}`);
      console.log(`[DEBUG] Has ID Token: ${idToken ? 'Yes' : 'No'}`);
    }

    if (!idToken) {
      throw new Error('Cognito ID Token is required for API access');
    }

    try {
      const result = await asyncKnowledgeBase.performQuery(
        message,
        idToken,
        maxResults,
        session.sessionId
      );

      // セッションを更新
      const updatedSession = addToSession(session, message, result);
      setSession(updatedSession);
      
      // 最新のレスポンス情報を保存
      setLastResponse(result);
      
      // 引用情報を整理
      if (result.citations) {
        const formattedCitations = formatCitations(result.citations);
        setCitations(formattedCitations);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw error;
    }
  }, [session, asyncKnowledgeBase]);

  // 非同期処理開始のみ
  const startAsyncQuery = useCallback(async (
    message: string,
    idToken?: string,
    maxResults?: number
  ): Promise<string> => {
    if (appConfig.debugMode) {
      console.log(`[DEBUG] useKnowledgeBase.startAsyncQuery: ${message}`);
    }

    if (!idToken) {
      throw new Error('Cognito ID Token is required for API access');
    }

    return await asyncKnowledgeBase.startAsyncQuery(
      message,
      idToken,
      maxResults,
      session.sessionId
    );
  }, [session, asyncKnowledgeBase]);

  // ステータス確認
  const checkQueryStatus = useCallback(async (
    requestId: string,
    idToken?: string
  ): Promise<any> => {
    if (appConfig.debugMode) {
      console.log(`[DEBUG] useKnowledgeBase.checkQueryStatus: ${requestId}`);
    }

    if (!idToken) {
      throw new Error('Cognito ID Token is required for API access');
    }

    const statusResult = await asyncKnowledgeBase.checkAsyncStatusWithError(requestId, idToken);
    
    if (statusResult.success) {
      return statusResult.data;
    } else {
      throw new Error(statusResult.error.message || statusResult.error.error);
    }
  }, [asyncKnowledgeBase]);

  // セッションクリア
  const clearSession = useCallback(() => {
    if (appConfig.debugMode) {
      console.log('[DEBUG] useKnowledgeBase.clearSession');
    }
    setSession(createChatSession());
    setLastResponse(null);
    setCitations([]);
    asyncKnowledgeBase.clearResult();
  }, [asyncKnowledgeBase]);

  // エラークリア
  const clearError = useCallback(() => {
    if (appConfig.debugMode) {
      console.log('[DEBUG] useKnowledgeBase.clearError');
    }
    asyncKnowledgeBase.clearError();
  }, [asyncKnowledgeBase]);

  // クエリキャンセル
  const cancelQuery = useCallback(() => {
    if (appConfig.debugMode) {
      console.log('[DEBUG] useKnowledgeBase.cancelQuery');
    }
    asyncKnowledgeBase.cancelQuery();
  }, [asyncKnowledgeBase]);

  return {
    // 状態
    loading,
    error,
    session,
    
    // 最新のレスポンス情報
    lastResponse,
    citations,
    
    // 非同期処理情報
    progress,
    currentStatus,
    requestId,
    
    // 関数
    sendMessageWithCitations,
    startAsyncQuery,
    checkQueryStatus,
    clearSession,
    clearError,
    cancelQuery,
  };
};
